import { useEffect, useState } from 'react';
import { getUiSettings } from '../services/api';
import telegramService from '../services/telegram';

/**
 * Блок «Связаться с нами» в подвале страниц.
 *
 * Кнопки на поверхности `card` — той же, что у строк профиля выше:
 * прозрачными в тёмной теме они сливались с фоном и не читались
 * кнопками. Это не нарушает «один акцент на экран» (брендбук 1):
 * акцентом считается цветная заливка `action`, а `card` — поверхность,
 * и цветного акцента здесь по-прежнему нет.
 *
 * Граница обязательна и остаётся: в светлой теме `card` и `bg` оба
 * белые, без неё кнопка исчезает (брендбук 2.8).
 *
 * Своих горизонтальных полей у блока нет — их задаёт страница. Раньше
 * стоял `px-lg`, и на «Рядом», где футер лежит внутри `px-4`, отступ
 * удваивался: блок был на 32px у́же остальной страницы.
 */

// Первые две кнопки встают парой в ряд, остальные — во всю ширину.
// Так задумано в настройках: Telegram и WhatsApp коротко подписаны,
// «Записаться на экскурсию» в половину ширины не помещается
const PAIRED = 2;

const ContactFooter = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getUiSettings()
      .then(response => setSettings(response.data))
      // Подвал необязателен: не загрузился — страница просто без него,
      // сообщать пользователю не о чем
      .catch(() => {});
  }, []);

  if (!settings) return null;

  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    return imageData.url || imageData.data?.url || null;
  };

  const handleButtonClick = (url) => {
    telegramService.hapticFeedback('light');
    telegramService.openLink(url);
  };

  const buttons = [
    { icon: getImageUrl(settings.telegramIcon), url: settings.telegramUrl, label: 'Telegram' },
    { icon: getImageUrl(settings.whatsappIcon), url: settings.whatsappUrl, label: 'WhatsApp' },
    {
      icon: getImageUrl(settings.tourBookingIcon),
      url: settings.tourBookingUrl,
      label: 'Записаться на экскурсию',
    },
    {
      icon: getImageUrl(settings.addToAppIcon),
      url: settings.addToAppUrl,
      label: 'Хочу в приложение!',
    },
  ];

  const visibleButtons = buttons.filter(btn => btn.icon && btn.url);
  if (visibleButtons.length === 0) return null;

  // Иконки приходят из Strapi картинками, а не глифами Phosphor: это
  // логотипы сторонних сервисов, у набора их нет и быть не должно
  const renderButton = (btn) => (
    <button
      key={btn.label}
      type="button"
      onClick={() => handleButtonClick(btn.url)}
      className="flex h-12 w-full items-center gap-3 rounded-md border-0 bg-theme-card-secondary px-4 text-label text-theme-text-primary transition-colors duration-fast ease-out active:scale-98"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <img src={btn.icon} alt="" className="h-6 w-6 flex-shrink-0 object-contain" />
      <span className="truncate">{btn.label}</span>
    </button>
  );

  const paired = visibleButtons.slice(0, PAIRED);
  const full = visibleButtons.slice(PAIRED);

  return (
    <section className="mt-8">
      <h2 className="mb-4 text-h2 text-theme-text-primary">Связаться с нами</h2>

      <div className="flex flex-col gap-2">
        {paired.length > 0 && (
          <div className="grid grid-cols-2 gap-2">{paired.map(renderButton)}</div>
        )}
        {full.map(renderButton)}
      </div>
    </section>
  );
};

export default ContactFooter;
